import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { LayoutService } from '../layout.service';
import { RecordPageComponent } from '../record-page/record-page.component';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import * as g from '../../globals';

interface FeasibilityRequest {
  customerName: string;
  coordinates: string;
  speed: string;
  contract: string;
  sla: string;
  serverLayer: string;
}

interface FeasibilityOption {
  Code: string;
  Name: string;
}

interface FeasibilityResult {
  id: string | number;
  Provider: string;
  Type: string;
  Speed: string;
  SLA: string;
  MRC: number;
  NRC: number;
}

@Component({
  selector: 'app-vault-xfeasibility-client',
  templateUrl: './vault-xfeasibility-client.component.html',
  styleUrls: ['./vault-xfeasibility-client.component.less'],
})
export class VaultXFeasibilityClientComponent implements OnInit {
  feasibilityForm: FormGroup;
  speedOptions: FeasibilityOption[] = [];
  slaOptions: FeasibilityOption[] = [];
  contractOptions: FeasibilityOption[] = [];
  readonly serverLayerOptions = [/*'Layer 2',*/ 'Layer 3'];

  isLoading = false;
  isOptionsLoading = false;
  isCreating = false;
  isDocumentCreated = false;
  documentNum = 0;
  hasSubmitted = false;
  errorMessage = '';
  infoMessage = '';
  createDocumentErrorMessage = '';
  createDocumentSuccessMessage = '';
  results: FeasibilityResult[] = [];
  selectedResult: FeasibilityResult = null;
  global = g;
  speedQryId = 0;
  slaQryId = 0;
  termQryId = 0;

  constructor(
    private layoutService: LayoutService,
    private drawerService: NzDrawerService,
  ) {
    this.feasibilityForm = new FormGroup({
      customerName: new FormControl('', [
        Validators.required,
        this.requiredTextValidator,
      ]),
      coordinates: new FormControl('', [
        Validators.required,
        this.coordinatesValidator,
      ]),
      speed: new FormControl(null, [Validators.required]),
      contract: new FormControl(null, [Validators.required]),
      sla: new FormControl(null, [Validators.required]),
      serverLayer: new FormControl(null, [Validators.required]),
    });
    this.feasibilityForm.patchValue({
      serverLayer: this.serverLayerOptions[0],
    });
  }

  ngOnInit(): void {
    this.speedQryId = this.global.feasibilitySpeedQryID;
    this.slaQryId = this.global.feasibilitySLAQryID;
    this.termQryId = this.global.feasibilityTermQryID;

    this.loadOptionLists();
  }

  submitFeasibilityRequest(): void {
    this.errorMessage = '';
    this.infoMessage = '';
    this.hasSubmitted = true;

    if (this.feasibilityForm.invalid) {
      this.markFormAsDirty();
      this.errorMessage =
        'Please complete all required fields before checking feasibility.';
      return;
    }

    const requestBody = this.buildRequestBody();
    this.isLoading = true;
    this.results = [];
    this.selectedResult = null;

    this.layoutService
      .checkFeasibility(requestBody)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (response: any) => {
          this.results = response && response.results ? response.results : [];
          if (!this.results.length) {
            this.infoMessage =
              'No feasible connectivity options were found for the supplied location and requirements. Please verify the details or contact our sales team for assistance.';
          }
        },
        (error) => {
          console.error('Feasibility check failed.', {
            status: error && error.status,
          });
          this.errorMessage =
            'Unable to complete the feasibility request. Please try again.';
        },
      );
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.feasibilityForm.get(controlName);
    return (
      !!control &&
      control.invalid &&
      (control.dirty || control.touched || this.hasSubmitted)
    );
  }

  selectResult(result: FeasibilityResult): void {
    this.selectedResult = result;
  }

  createDocument(): void {
    if (!this.selectedResult) {
      return;
    }

    const requestBody = this.buildRequestBody();

    this.isCreating = true;
    this.createDocumentErrorMessage = '';
    this.createDocumentSuccessMessage = '';

    this.layoutService
      .createFeasibilityDocument(
        requestBody,
        this.selectedResult,
        g.feasibilityDocType,
      )
      .pipe(finalize(() => (this.isCreating = false)))
      .subscribe(
        (response: any) => {
          if (response && response.success === false) {
            this.isDocumentCreated = false;
            this.createDocumentErrorMessage =
              response.message || 'Unable to create feasibility document.';
            return;
          }

          if (response && response.success === true) {
            this.isDocumentCreated = true;
            this.documentNum = response.documentNum;
            this.createDocumentSuccessMessage =
              response.message || 'Feasibility document created successfully.';
          }
        },
        (error) => {
          const response = error && error.error;
          this.isDocumentCreated = false;
          this.createDocumentErrorMessage =
            (response && response.message) ||
            'Unable to create feasibility document.';
        },
      );
  }

  backToInitialPage(): void {
    this.isDocumentCreated = false;
    this.isCreating = false;
    this.isLoading = false;
    this.createDocumentErrorMessage = '';
    this.createDocumentSuccessMessage = '';
    this.selectedResult = null;
    this.results = [];
    this.errorMessage = '';
    this.infoMessage = '';
    this.hasSubmitted = false;
    this.feasibilityForm.reset();
  }

  viewCreatedDocument(): void {
    RecordPageComponent.openRecordPage(this.drawerService, {
      actionType: 'View',
      drawerTitle: 'View Details',
      inDrawer: true,
      moduleId: g.feasibilityModuleID,
      parentComponent: this,
      recordId: this.documentNum.toString(),
      recordModel: undefined,
      linkedField: '',
    });
  }

  previewCreatedDocument(): void {
    window.open(
      g.apiBaseUrl +
        '/Report/PrintObject/' +
        g.feasibilityLayoutID +
        '?moduleId=' +
        g.feasibilityModuleID +
        '&exportMode=5&mode=Preview&selectedId=' +
        this.documentNum.toString() +
        '&' +
        g.getUrlSuffix(),
    );
  }

  private buildRequestBody(): FeasibilityRequest {
    const value = this.feasibilityForm.value;

    return {
      customerName: String(value.customerName).trim(),
      coordinates: String(value.coordinates).trim(),
      speed: value.speed,
      contract: value.contract,
      sla: value.sla,
      serverLayer: value.serverLayer,
    };
  }

  private loadOptionLists(): void {
    this.isOptionsLoading = true;
    let pendingQueries = 0;

    const loadOptions = (
      queryId: number,
      setOptions: (options: FeasibilityOption[]) => void,
    ) => {
      if (!queryId) {
        setOptions([]);
        return;
      }

      pendingQueries++;
      this.layoutService.executeQuery(queryId).subscribe(
        (result: any) => {
          setOptions(Array.isArray(result) ? result : []);
          pendingQueries--;
          this.isOptionsLoading = pendingQueries > 0;
        },
        (error) => {
          console.error('Failed to load feasibility option list.', {
            queryId,
            status: error && error.status,
          });
          setOptions([]);
          pendingQueries--;
          this.isOptionsLoading = pendingQueries > 0;
        },
      );
    };

    loadOptions(this.speedQryId, (options) => (this.speedOptions = options));
    loadOptions(this.slaQryId, (options) => (this.slaOptions = options));
    loadOptions(this.termQryId, (options) => (this.contractOptions = options));

    if (pendingQueries === 0) {
      this.isOptionsLoading = false;
    }
  }

  private markFormAsDirty(): void {
    Object.keys(this.feasibilityForm.controls).forEach((controlName) => {
      const control = this.feasibilityForm.get(controlName);
      if (control) {
        control.markAsDirty();
        control.updateValueAndValidity();
      }
    });
  }

  private requiredTextValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    return typeof control.value === 'string' &&
      control.value.trim().length === 0
      ? { required: true }
      : null;
  }

  private coordinatesValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const value = typeof control.value === 'string' ? control.value.trim() : '';

    if (!value) {
      return { required: true };
    }

    const match = value.match(
      /^(-?(?:\d+(?:\.\d+)?))\s*,\s*(-?(?:\d+(?:\.\d+)?))$/,
    );

    if (!match) {
      return { coordinates: true };
    }

    const latitude = Number(match[1]);
    const longitude = Number(match[2]);

    return latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180
      ? null
      : { coordinates: true };
  }
}
